import { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { FileText, Download, ExternalLink, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export const PdfViewer = ({ version, hasPdf }) => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error | empty
  const [pageCanvases, setPageCanvases] = useState([]);

  const pdfUrl = `${API}/menu.pdf?v=${version || 0}`;
  const downloadUrl = `${API}/menu.pdf?v=${version || 0}&download=1`;

  const renderPdf = useCallback(async () => {
    if (!hasPdf) {
      setStatus("empty");
      return;
    }
    setStatus("loading");
    try {
      const resp = await fetch(pdfUrl, { cache: "force-cache" });
      if (!resp.ok) throw new Error("fetch failed");
      const buffer = await resp.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

      const width = containerRef.current
        ? containerRef.current.clientWidth
        : 600;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const canvases = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = (width / base.width) * dpr;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;
        canvases.push(canvas.toDataURL("image/jpeg", 0.85));
      }
      setPageCanvases(canvases);
      setStatus("ready");
    } catch (e) {
      console.error("PDF render error", e);
      setStatus("error");
    }
  }, [pdfUrl, hasPdf]);

  useEffect(() => {
    renderPdf();
  }, [renderPdf]);

  return (
    <section data-testid="pdf-viewer-section" className="w-full">
      <div ref={containerRef} className="w-full">
        {status === "loading" && (
          <div
            data-testid="pdf-loading"
            className="flex flex-col items-center justify-center py-20 gap-4"
          >
            <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
            <p className="font-serif text-xl text-[#d4af37] gd-pulse">
              {t("loadingMenu")}
            </p>
          </div>
        )}

        {status === "empty" && (
          <div
            data-testid="pdf-empty"
            className="text-center py-16 px-6 border border-[#d4af37]/20 rounded-md bg-[#0f172a]"
          >
            <FileText className="w-10 h-10 text-[#d4af37]/60 mx-auto mb-4" />
            <p className="font-serif text-2xl text-[#d4af37] mb-2">
              {t("menuComingTitle")}
            </p>
            <p className="text-sm text-[#e0ddcf]/80">
              {t("menuComingText")}
            </p>
          </div>
        )}

        {status === "ready" && (
          <div className="flex flex-col gap-4">
            {pageCanvases.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Pagina menu ${i + 1}`}
                data-testid={`pdf-page-${i + 1}`}
                loading={i < 2 ? "eager" : "lazy"}
                className="w-full h-auto rounded-md drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)] gd-fade-up"
                style={{ animationDelay: `${Math.min(i, 4) * 60}ms` }}
              />
            ))}
          </div>
        )}

        {status === "error" && (
          <div
            data-testid="pdf-error"
            className="text-center py-12 px-6 border border-[#d4af37]/30 rounded-md bg-[#0f172a]"
          >
            <p className="text-sm text-[#e0ddcf] mb-5">
              {t("pdfErrorText")}
            </p>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-pdf-button"
              className="inline-flex items-center justify-center gap-2 bg-[#d4af37] text-[#060a14] font-semibold px-6 py-3 rounded-sm active:scale-95 transition-transform w-full"
            >
              <ExternalLink className="w-4 h-4" /> {t("openPdf")}
            </a>
          </div>
        )}
      </div>

      {(status === "ready" || status === "error") && (
        <div className="mt-6 flex flex-col gap-3">
          <a
            href={downloadUrl}
            data-testid="download-pdf-button"
            className="inline-flex items-center justify-center gap-2 bg-transparent border border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-sm hover:bg-[#d4af37]/10 active:bg-[#d4af37]/20 transition-colors w-full font-medium"
          >
            <Download className="w-4 h-4" /> {t("downloadMenu")}
          </a>
        </div>
      )}
    </section>
  );
};

export default PdfViewer;
